import { useCallback, useEffect, useRef, useState } from "react"
import { Bell, RefreshCw } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { getActivityLog, type ActivityLogEntry } from "@/lib/api"

const EVENT_LABELS: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
  generated: { label: "Generated", variant: "default" },
  extended: { label: "Extended", variant: "secondary" },
  overwritten: { label: "Updated", variant: "outline" },
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return "just now"
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export function ActivityInbox() {
  const [open, setOpen] = useState(false)
  const [entries, setEntries] = useState<ActivityLogEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [lastFetched, setLastFetched] = useState<number>(0)
  const panelRef = useRef<HTMLDivElement>(null)

  const fetchEntries = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getActivityLog(30)
      setEntries(data)
      setLastFetched(Date.now())
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open && Date.now() - lastFetched > 30000) {
      fetchEntries()
    }
  }, [open, lastFetched, fetchEntries])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [open])

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        title="Activity log"
      >
        <Bell className="h-4 w-4" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-lg border bg-popover p-0 text-popover-foreground shadow-lg z-50">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="text-sm font-medium">Activity Log</span>
            <button
              onClick={fetchEntries}
              disabled={loading}
              className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {entries.length === 0 && !loading && (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                No activity yet
              </div>
            )}

            {loading && entries.length === 0 && (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                Loading...
              </div>
            )}

            {entries.map((entry) => {
              const config = EVENT_LABELS[entry.event_type] || {
                label: entry.event_type,
                variant: "outline" as const,
              }
              return (
                <div
                  key={entry.id}
                  className="flex items-start gap-2 border-b px-3 py-2 last:border-b-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium">
                        {entry.user_name}
                      </span>
                      <Badge variant={config.variant}>{config.label}</Badge>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {entry.metadata?.source
                        ? String(entry.metadata.source).replace(/_/g, " ")
                        : "workout"}
                      {" "}
                      <span className="text-muted-foreground/60">
                        {formatRelativeTime(entry.created_at)}
                      </span>
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
